import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { AddonKind, AddonTier, AddonTierFromApi } from './types';
import {
  BLACKLIST_TIERS,
  INBOX_PLACEMENT_TIERS,
  PHONE_VALIDATION_TIERS,
  WARMUP_INBOX_TIERS,
} from './types';

/**
 * Fallback ladders used while the API request is in flight OR when the API is
 * unreachable. Keep in lockstep with what `pnpm seed:plans` writes for a fresh
 * environment — same numbers the super-admin can override on /admin/addon-prices.
 */
const FALLBACK: Record<AddonKind, ReadonlyArray<AddonTier>> = {
  blacklist: BLACKLIST_TIERS,
  inbox_placement: INBOX_PLACEMENT_TIERS,
  phone_validation: PHONE_VALIDATION_TIERS,
  warmup_inbox: WARMUP_INBOX_TIERS,
};

/**
 * Fetch the live add-on tier ladders, keyed by addon kind. Returns the Off tier
 * (`quota: 0, monthlyCents: 0`) prepended on every list so the slider's "move it
 * back to Off" position renders correctly.
 *
 * `route` selects which endpoint to hit:
 *   - `'tenant'` → `/billing/addons/tiers` (JWT, used by Profile → Add-ons sliders)
 *   - `'public'` → `/plans/addons` (unauth, used by /pricing AddonsSection)
 *
 * Both endpoints return the same shape and are served from the same DB rows, so a
 * super-admin edit on /admin/addon-prices propagates everywhere on the next
 * tanstack-query refetch.
 */
export function useAddonTiers(route: 'tenant' | 'public') {
  const query = useQuery({
    queryKey: route === 'tenant' ? ['addon-tiers'] : ['plans', 'addons-public'],
    queryFn: () =>
      api<AddonTierFromApi[]>(
        route === 'tenant' ? '/billing/addons/tiers' : '/plans/addons',
        route === 'public' ? { auth: false } : undefined,
      ),
    staleTime: 60_000,
  });

  const byKind = groupTiers(query.data);
  return {
    isLoading: query.isLoading,
    error: query.error,
    /** Per-addon ladder with the Off tier prepended. */
    tiers: byKind,
    /** Single-addon convenience getter. */
    forAddon: (addon: AddonKind): ReadonlyArray<AddonTier> => byKind[addon] ?? [],
  };
}

function groupTiers(rows: AddonTierFromApi[] | undefined): Record<AddonKind, AddonTier[]> {
  if (!rows || rows.length === 0) {
    return {
      blacklist: [...FALLBACK.blacklist],
      inbox_placement: [...FALLBACK.inbox_placement],
      phone_validation: [...FALLBACK.phone_validation],
      warmup_inbox: [...FALLBACK.warmup_inbox],
    };
  }
  const byKind: Record<AddonKind, AddonTier[]> = {
    blacklist: [{ quota: 0, monthlyCents: 0, label: 'Off' }],
    inbox_placement: [{ quota: 0, monthlyCents: 0, label: 'Off' }],
    phone_validation: [{ quota: 0, monthlyCents: 0, label: 'Off' }],
    warmup_inbox: [{ quota: 0, monthlyCents: 0, label: 'Off' }],
  };
  for (const r of rows) {
    byKind[r.addon].push({
      quota: r.quota,
      monthlyCents: r.monthlyAmountCents,
      label: r.label,
    });
  }
  // Sort each ladder ascending by quota so the slider tick order matches the table view.
  for (const k of Object.keys(byKind) as AddonKind[]) {
    byKind[k].sort((a, b) => a.quota - b.quota);
  }
  return byKind;
}
